import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, ChevronRight, ChevronLeft, ArrowUpDown, Package2 } from "lucide-react";

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { value: "stock_desc", label: "الأعلى مخزوناً أولاً" },
  { value: "stock_asc",  label: "الأقل مخزوناً أولاً" },
  { value: "name_asc",   label: "الاسم (أ ← ي)" },
  { value: "name_desc",  label: "الاسم (ي ← أ)" },
  { value: "default",    label: "الترتيب الافتراضي" },
];

function StatusBadge({ status }) {
  const map = {
    success: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "نجح" },
    failed:  { bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500",     label: "فشل" },
    removed: { bg: "bg-slate-100",  text: "text-[var(--text-secondary)]",   dot: "bg-slate-400",   label: "محذوف" },
    pending: { bg: "bg-slate-100",  text: "text-[var(--text-secondary)]",   dot: "bg-slate-400",   label: "بانتظار" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
      {s.label}
    </span>
  );
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("stock_desc");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (term, pageNum, sortVal) => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", {
        params: { search: term, sort: sortVal, limit: PAGE_SIZE, skip: pageNum * PAGE_SIZE },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const qRef = useRef(q), pageRef = useRef(page), sortRef = useRef(sort);
  qRef.current = q; pageRef.current = page; sortRef.current = sort;

  useEffect(() => {
    load("", 0, sortRef.current);
    const id = setInterval(() => load(qRef.current, pageRef.current, sortRef.current), 15000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(q, 0, sort); }, 250);
    return () => clearTimeout(t);
  }, [q, sort, load]);

  const goToPage = (p) => { setPage(p); load(q, p, sort); };
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">
        <header className="fade-up">
          <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">المنتجات</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-1">إدارة المخزون الموحد</h1>
          <p className="text-[var(--text-secondary)] mt-1.5 text-sm">كمية كل منتج عبر كل الفروع — والمزامنة إلى سلة.</p>
        </header>

        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)]/80 overflow-hidden">
          {/* Search toolbar */}
          <div className="px-5 py-4 border-b border-[var(--border-color)] flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
              <input
                data-testid="product-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="ابحث بـ SKU، الباركود، أو اسم المنتج…"
                className="w-full bg-[#F8FAFC] border border-[var(--border-color)] rounded-xl ps-10 pe-3 py-2.5 text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 focus:bg-[var(--bg-card)] transition-all"
              />
            </div>
            <div className="relative">
              <ArrowUpDown className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-muted)] pointer-events-none" />
              <select
                data-testid="product-sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="bg-[#F8FAFC] border border-[var(--border-color)] rounded-xl ps-9 pe-8 py-2.5 text-sm focus:outline-none focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <span className="text-xs text-[var(--text-muted)] mono" data-testid="products-total">
              {total.toLocaleString("en-US")} منتج · {from}–{to}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead>
                <tr className="border-b border-[var(--border-color)] bg-[var(--bg-card-hover)]/50">
                  <th className="th-overline text-start px-5 py-3">SKU</th>
                  <th className="th-overline text-start px-5 py-3">المنتج</th>
                  <th className="th-overline text-start px-5 py-3">توزيع الفروع</th>
                  <th className="th-overline text-end px-5 py-3">إجمالي</th>
                  <th className="th-overline text-end px-5 py-3">مُزامَن</th>
                  <th className="th-overline text-end px-5 py-3">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[var(--text-muted)]">
                      <Loader2 className="w-5 h-5 animate-spin inline-block" />
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <Package2 className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                      <p className="text-[var(--text-muted)] text-sm">لا توجد منتجات.</p>
                    </td>
                  </tr>
                )}
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-[var(--bg-card-hover)]/50 transition-colors">
                    <td className="px-5 py-4 mono text-xs text-[var(--text-secondary)] font-medium">{p.sku}</td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[var(--text-primary)]">{p.name_ar || p.name || "—"}</div>
                      {p.barcode && <div className="text-[11px] text-[var(--text-muted)] mono mt-0.5">{p.barcode}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(p.branch_stock || []).map((bs) => (
                          <span
                            key={bs.branch_id}
                            className="inline-flex items-center gap-1 text-[11px] bg-slate-100 border border-[var(--border-color)]/80 rounded-lg px-2 py-0.5 font-medium"
                          >
                            <span className="text-[var(--text-secondary)]">{bs.branch_name}</span>
                            <span className="mono text-[var(--text-secondary)]">{bs.qty}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-end">
                      <span className={`mono font-bold text-base ${p.total_stock === 0 ? "text-slate-300" : "text-[var(--text-primary)]"}`}>
                        {p.total_stock}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-end">
                      <span className="mono text-xs text-[var(--text-muted)]">{p.synced_total ?? 0}</span>
                    </td>
                    <td className="px-5 py-4 text-end" data-testid="product-row-status">
                      <StatusBadge status={p.last_sync_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className="px-5 py-4 border-t border-[var(--border-color)] flex items-center justify-between gap-3" data-testid="pagination">
              <div className="text-xs text-[var(--text-muted)] mono">صفحة {page + 1} من {maxPage + 1}</div>
              <div className="flex gap-2">
                <button
                  onClick={() => goToPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  data-testid="pagination-prev"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border-color)] rounded-lg text-xs hover:border-slate-400 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  السابق
                </button>
                <button
                  onClick={() => goToPage(Math.min(maxPage, page + 1))}
                  disabled={page >= maxPage}
                  data-testid="pagination-next"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border-color)] rounded-lg text-xs hover:border-slate-400 disabled:opacity-40 transition-colors"
                >
                  التالي
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
